<template>
  <main class="trust-center">
    <PageHeader icon="fas fa-shield-alt">
      <template #title>Keamanan, Privasi & Kepatuhan Medhop</template>
      <template #subtitle>
        Pusat informasi transparan mengenai landasan regulasi, standar, dan
        kapabilitas Medhop dalam membantu klinik mengelola layanan kesehatan.
      </template>
      <template #actions>
        <b-button variant="outline-primary" to="/help">
          <i class="fas fa-comments mr-2"></i>Tanya tim Medhop
        </b-button>
      </template>
    </PageHeader>

    <section class="trust-hero">
      <div class="trust-hero__content">
        <div class="eyebrow"><i class="fas fa-check-circle"></i> Trust Center Medhop</div>
        <h2>Kepercayaan klinik dibangun dengan transparansi.</h2>
        <p>
          Medhop dirancang untuk mendukung operasional dan rekam medis elektronik
          klinik dengan mengacu pada regulasi Indonesia serta praktik keamanan dan
          interoperabilitas yang relevan untuk layanan kesehatan digital.
        </p>
        <div class="hero-meta">
          <span><i class="fas fa-calendar-check"></i> Ditinjau 29 Agustus 2026</span>
          <span><i class="fas fa-map-marker-alt"></i> Berlaku untuk Indonesia</span>
        </div>
      </div>
      <article class="pse-card">
        <span class="pse-card__icon"><i class="fas fa-landmark"></i></span>
        <div>
          <small>Status legal penyelenggara sistem</small>
          <h3>Terdaftar sebagai PSE Lingkup Privat</h3>
          <p>Kementerian Komunikasi dan Digital Republik Indonesia</p>
        </div>
        <span class="verified-badge"><i class="fas fa-check"></i> Terdaftar</span>
      </article>
    </section>

    <section class="trust-summary" aria-label="Ringkasan kepatuhan">
      <article>
        <span class="summary-icon teal"><i class="fas fa-gavel"></i></span>
        <div><strong>6</strong><small>Acuan regulasi nasional</small></div>
      </article>
      <article>
        <span class="summary-icon blue"><i class="fas fa-fingerprint"></i></span>
        <div><strong>Privasi</strong><small>Data kesehatan sebagai data spesifik</small></div>
      </article>
      <article>
        <span class="summary-icon purple"><i class="fas fa-exchange-alt"></i></span>
        <div><strong>HL7 FHIR</strong><small>Acuan interoperabilitas kesehatan</small></div>
      </article>
      <article>
        <span class="summary-icon amber"><i class="fas fa-users-cog"></i></span>
        <div><strong>RBAC</strong><small>Akses berbasis peran & cabang</small></div>
      </article>
    </section>

    <section class="trust-section">
      <div class="section-heading">
        <div>
          <span class="section-kicker">Landasan Indonesia</span>
          <h2>Regulasi yang menjadi acuan</h2>
          <p>
            Daftar ini menjelaskan area regulasi yang relevan—bukan pernyataan bahwa
            satu aplikasi saja otomatis membuat seluruh kegiatan klinik patuh.
          </p>
        </div>
      </div>

      <div class="regulation-grid">
        <article v-for="item in regulations" :key="item.title" class="regulation-card">
          <header>
            <span class="regulation-card__icon"><i :class="item.icon"></i></span>
            <span class="status-pill" :class="item.statusClass">{{ item.status }}</span>
          </header>
          <h3>{{ item.title }}</h3>
          <p>{{ item.description }}</p>
          <footer>
            <span>{{ item.scope }}</span>
            <a :href="item.url" target="_blank" rel="noopener noreferrer">
              Sumber resmi <i class="fas fa-external-link-alt"></i>
            </a>
          </footer>
        </article>
      </div>
    </section>

    <section class="trust-section controls-section">
      <div class="section-heading">
        <div>
          <span class="section-kicker">Kapabilitas produk</span>
          <h2>Kontrol yang membantu tata kelola klinik</h2>
          <p>
            Fitur harus dikonfigurasi dan digunakan secara konsisten agar kontrol
            organisasi berjalan efektif.
          </p>
        </div>
      </div>
      <div class="control-grid">
        <article v-for="control in controls" :key="control.title">
          <span><i :class="control.icon"></i></span>
          <div>
            <h3>{{ control.title }}</h3>
            <p>{{ control.description }}</p>
          </div>
        </article>
      </div>
    </section>

    <section class="trust-section standards-section">
      <div class="section-heading">
        <div>
          <span class="section-kicker">Acuan non-pemerintah</span>
          <h2>Standar & praktik industri</h2>
          <p>
            Digunakan sebagai arah peningkatan mutu. Label “acuan” tidak berarti
            Medhop telah memperoleh sertifikasi dari organisasi penerbit standar.
          </p>
        </div>
      </div>
      <div class="standard-grid">
        <article v-for="standard in standards" :key="standard.title">
          <div class="standard-card__top">
            <span>{{ standard.short }}</span>
            <em>Acuan</em>
          </div>
          <h3>{{ standard.title }}</h3>
          <p>{{ standard.description }}</p>
          <a :href="standard.url" target="_blank" rel="noopener noreferrer">
            Pelajari standar <i class="fas fa-arrow-right"></i>
          </a>
        </article>
      </div>
    </section>

    <section class="responsibility-section">
      <div class="responsibility-intro">
        <span class="section-kicker">Tanggung jawab bersama</span>
        <h2>Kepatuhan yang efektif membutuhkan Medhop dan klinik.</h2>
        <p>
          Medhop menyediakan sarana teknologi. Klinik tetap memegang tanggung jawab
          atas dasar pemrosesan, kualitas pencatatan, pemberian akses, serta kebijakan
          operasional di organisasinya.
        </p>
      </div>
      <div class="responsibility-columns">
        <article>
          <header><span><i class="fas fa-laptop-medical"></i></span><h3>Peran Medhop</h3></header>
          <ul>
            <li v-for="item in medhopResponsibilities" :key="item"><i class="fas fa-check"></i>{{ item }}</li>
          </ul>
        </article>
        <article>
          <header><span><i class="fas fa-clinic-medical"></i></span><h3>Peran Klinik</h3></header>
          <ul>
            <li v-for="item in clinicResponsibilities" :key="item"><i class="fas fa-check"></i>{{ item }}</li>
          </ul>
        </article>
      </div>
    </section>

    <section class="disclosure-note">
      <i class="fas fa-info-circle"></i>
      <div>
        <h3>Catatan transparansi</h3>
        <p>
          Halaman ini adalah ringkasan informasi, bukan opini atau jaminan hukum dan
          bukan pengganti audit independen. Status integrasi, konfigurasi, dan lingkup
          penerapan dapat berbeda pada setiap klinik. Regulasi dan dokumentasi teknis
          dapat berubah; selalu periksa sumber resmi yang ditautkan.
        </p>
      </div>
    </section>

    <section class="trust-cta">
      <div>
        <span>Perlu dokumen untuk proses pengadaan atau penilaian vendor?</span>
        <h2>Bicarakan kebutuhan kepatuhan klinik Anda dengan tim Medhop.</h2>
      </div>
      <b-button variant="primary" to="/help">Hubungi tim Medhop <i class="fas fa-arrow-right ml-2"></i></b-button>
    </section>
  </main>
</template>

<script>
import PageHeader from "@/component/general/PageHeader.vue";

export default {
  name: "TrustCenter",
  components: { PageHeader },
  data() {
    return {
      regulations: [
        {
          title: "Permenkes No. 24 Tahun 2022",
          description: "Landasan penyelenggaraan rekam medis elektronik, termasuk prinsip keamanan dan kerahasiaan data kesehatan.",
          scope: "Rekam Medis Elektronik",
          status: "Acuan utama",
          statusClass: "is-primary",
          icon: "fas fa-file-medical-alt",
          url: "https://jdih.kemkes.go.id/documents/peraturan-menteri-kesehatan-nomor-24-tahun-2022",
        },
        {
          title: "UU No. 27 Tahun 2022",
          description: "Mengatur pelindungan data pribadi, termasuk data kesehatan yang dikategorikan sebagai data pribadi spesifik.",
          scope: "Pelindungan Data Pribadi",
          status: "Acuan utama",
          statusClass: "is-primary",
          icon: "fas fa-user-lock",
          url: "https://peraturan.bpk.go.id/Details/229798/uu-no-27-tahun-2022",
        },
        {
          title: "PP No. 71 Tahun 2019",
          description: "Mengatur kewajiban, keandalan, keamanan, dan tata kelola penyelenggaraan sistem dan transaksi elektronik.",
          scope: "Sistem Elektronik",
          status: "Acuan",
          statusClass: "is-reference",
          icon: "fas fa-server",
          url: "https://peraturan.bpk.go.id/Details/122030/pp-no-71-tahun-2019",
        },
        {
          title: "Permenkominfo No. 5 Tahun 2020",
          description: "Dasar pendaftaran dan tata kelola Penyelenggara Sistem Elektronik Lingkup Privat. Medhop berstatus terdaftar.",
          scope: "PSE Lingkup Privat",
          status: "Terdaftar",
          statusClass: "is-verified",
          icon: "fas fa-certificate",
          url: "https://jdih.komdigi.go.id/produk_hukum/view/id/759/t/peraturan%2Bmenteri%2Bkomunikasi%2Bdan%2Binformatika%2Bnomor%2B5%2Btahun%2B2020",
        },
        {
          title: "UU No. 17 Tahun 2023",
          description: "Kerangka hukum nasional sektor kesehatan, termasuk penyelenggaraan sistem informasi dan data kesehatan.",
          scope: "Kesehatan",
          status: "Acuan",
          statusClass: "is-reference",
          icon: "fas fa-balance-scale",
          url: "https://peraturan.bpk.go.id/Details/258028/uu-no-17-tahun-2023",
        },
        {
          title: "PP No. 28 Tahun 2024",
          description: "Peraturan pelaksanaan UU Kesehatan yang memperinci tata kelola penyelenggaraan layanan dan informasi kesehatan.",
          scope: "Pelaksanaan UU Kesehatan",
          status: "Acuan",
          statusClass: "is-reference",
          icon: "fas fa-book-medical",
          url: "https://peraturan.bpk.go.id/Details/294077/pp-no-28-tahun-2024",
        },
      ],
      controls: [
        { title: "Hak akses berbasis peran", description: "Akses pengguna dapat dibatasi sesuai tugas, jabatan, dan kebutuhan operasional.", icon: "fas fa-user-shield" },
        { title: "Pembatasan akses cabang", description: "Cakupan akses dapat diatur untuk mendukung tata kelola organisasi multi-cabang.", icon: "fas fa-code-branch" },
        { title: "Jejak audit aktivitas", description: "Aktivitas penting dapat ditelusuri untuk membantu pengawasan dan investigasi internal.", icon: "fas fa-clipboard-list" },
        { title: "Dokumen klinis terstruktur", description: "Pencatatan klinis membantu kelengkapan, konsistensi, dan keterlacakan informasi pasien.", icon: "fas fa-notes-medical" },
        { title: "Interoperabilitas SATUSEHAT", description: "Dukungan alur integrasi tersedia sesuai modul dan aktivasi pada masing-masing klinik.", icon: "fas fa-project-diagram" },
        { title: "Pengelolaan akun terpusat", description: "Administrator dapat mengatur pengguna, role, sesi, dan melakukan review akses berkala.", icon: "fas fa-users-cog" },
      ],
      standards: [
        {
          short: "FHIR",
          title: "HL7 FHIR",
          description: "Standar pertukaran data kesehatan yang digunakan SATUSEHAT untuk interoperabilitas rekam medis elektronik.",
          url: "https://satusehat.kemkes.go.id/platform/docs/id/interoperability/",
        },
        {
          short: "ISO",
          title: "ISO/IEC 27001:2022",
          description: "Kerangka rujukan sistem manajemen keamanan informasi berbasis risiko. Dicantumkan sebagai acuan, bukan klaim sertifikasi.",
          url: "https://www.iso.org/standard/27001",
        },
        {
          short: "OWASP",
          title: "OWASP ASVS",
          description: "Referensi praktik verifikasi keamanan aplikasi web dan kontrol teknis sepanjang pengembangan perangkat lunak.",
          url: "https://owasp.org/www-project-application-security-verification-standard/",
        },
      ],
      medhopResponsibilities: [
        "Menyediakan kontrol akses dan jejak audit yang dapat dikonfigurasi.",
        "Memelihara aplikasi serta mendukung integrasi sesuai lingkup layanan.",
        "Memberikan dukungan saat klinik membutuhkan informasi teknis produk.",
      ],
      clinicResponsibilities: [
        "Menentukan dasar pemrosesan data dan memberikan informasi kepada pasien.",
        "Menetapkan akses pengguna dengan prinsip kebutuhan minimum.",
        "Menjaga akurasi rekam medis, perangkat, akun, dan prosedur internal.",
      ],
    };
  },
};
</script>

<style lang="scss" scoped>
.trust-center {
  --trust-navy: #13283f;
  --trust-teal: #168f88;
  --trust-border: #e3ebf2;
  color: #334155;
  padding-bottom: 28px;
}

.trust-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(320px, 0.75fr);
  gap: 24px;
  padding: 36px;
  overflow: hidden;
  position: relative;
  border-radius: 22px;
  background: linear-gradient(125deg, #102a43 0%, #164e63 62%, #168f88 140%);
  box-shadow: 0 20px 45px rgba(15, 42, 67, 0.14);
  color: #fff;
}

.trust-hero::after {
  width: 360px;
  height: 360px;
  position: absolute;
  right: -160px;
  bottom: -230px;
  border: 55px solid rgba(255, 255, 255, 0.05);
  border-radius: 50%;
  content: "";
}

.trust-hero__content { position: relative; z-index: 1; }
.eyebrow, .section-kicker { color: #22b8ae; font-size: 12px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
.eyebrow { color: #a7f3d0; }
.eyebrow i { margin-right: 7px; }
.trust-hero h2 { max-width: 680px; margin: 14px 0 12px; color: #fff !important; font-size: clamp(28px, 3vw, 42px); font-weight: 800; letter-spacing: -.035em; line-height: 1.12; }
.trust-hero__content > p { max-width: 720px; margin: 0; color: #e7f1fb !important; font-size: 15px; line-height: 1.75; }
.hero-meta { display: flex; flex-wrap: wrap; gap: 18px; margin-top: 24px; color: #dbe7f2 !important; font-size: 12px; font-weight: 600; }
.hero-meta i { margin-right: 7px; color: #5eead4; }

.pse-card { align-self: stretch; display: flex; flex-direction: column; justify-content: center; min-height: 230px; padding: 26px; position: relative; z-index: 1; border: 1px solid rgba(255,255,255,.18); border-radius: 18px; background: rgba(255,255,255,.10); backdrop-filter: blur(10px); }
.pse-card__icon { width: 48px; height: 48px; display: grid; place-items: center; margin-bottom: 18px; border-radius: 14px; background: rgba(167,243,208,.16); color: #a7f3d0; font-size: 20px; }
.pse-card small { color: #d5edf7 !important; font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; }
.pse-card h3 { margin: 7px 0; color: #fff !important; font-size: 20px; line-height: 1.35; }
.pse-card p { margin: 0; color: #e0edf4 !important; font-size: 12px; }
.verified-badge { align-self: flex-start; margin-top: 20px; padding: 7px 11px; border-radius: 999px; background: #d1fae5; color: #047857; font-size: 11px; font-weight: 800; }
.verified-badge i { margin-right: 6px; }

.trust-summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin: 18px 0 30px; }
.trust-summary article { display: flex; align-items: center; min-height: 92px; padding: 18px; border: 1px solid var(--trust-border); border-radius: 16px; background: #fff; box-shadow: 0 8px 24px rgba(15, 42, 67, .045); }
.summary-icon { width: 43px; height: 43px; flex: 0 0 43px; display: grid; place-items: center; margin-right: 13px; border-radius: 12px; font-size: 17px; }
.summary-icon.teal { background: #e7f7f5; color: #12827c; }
.summary-icon.blue { background: #eaf3ff; color: #2563eb; }
.summary-icon.purple { background: #f2ecff; color: #7c3aed; }
.summary-icon.amber { background: #fff4dd; color: #b45309; }
.trust-summary strong, .trust-summary small { display: block; }
.trust-summary strong { color: var(--trust-navy); font-size: 18px; line-height: 1.2; }
.trust-summary small { margin-top: 4px; color: #7b8ba0; font-size: 11px; line-height: 1.3; }

.trust-section { margin-top: 34px; }
.section-heading { display: flex; justify-content: space-between; margin-bottom: 18px; }
.section-heading h2, .responsibility-intro h2 { margin: 6px 0 7px; color: var(--trust-navy); font-size: 25px; font-weight: 800; letter-spacing: -.02em; }
.section-heading p, .responsibility-intro p { max-width: 780px; margin: 0; color: #64748b; font-size: 13px; line-height: 1.65; }

.regulation-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
.regulation-card { display: flex; flex-direction: column; min-height: 260px; padding: 21px; border: 1px solid var(--trust-border); border-radius: 17px; background: #fff; transition: transform .18s ease, box-shadow .18s ease; }
.regulation-card:hover { transform: translateY(-2px); box-shadow: 0 14px 34px rgba(15, 42, 67, .08); }
.regulation-card header, .standard-card__top { display: flex; align-items: center; justify-content: space-between; }
.regulation-card__icon { width: 42px; height: 42px; display: grid; place-items: center; border-radius: 12px; background: #edf8f7; color: var(--trust-teal); font-size: 16px; }
.status-pill { padding: 6px 9px; border-radius: 999px; font-size: 10px; font-style: normal; font-weight: 800; }
.status-pill.is-primary { background: #e7f7f5; color: #08756f; }
.status-pill.is-reference { background: #eef2f7; color: #526175; }
.status-pill.is-verified { background: #d1fae5; color: #047857; }
.regulation-card h3 { margin: 19px 0 9px; color: var(--trust-navy); font-size: 16px; font-weight: 800; }
.regulation-card > p { flex: 1; margin: 0; color: #64748b; font-size: 12px; line-height: 1.7; }
.regulation-card footer { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 20px; padding-top: 14px; border-top: 1px solid #eef2f6; }
.regulation-card footer span { color: #64748b; font-size: 10px; font-weight: 700; }
.regulation-card a, .standard-grid a { color: var(--trust-teal); font-size: 11px; font-weight: 800; text-decoration: none; }
.regulation-card a i { margin-left: 4px; font-size: 9px; }

.controls-section { padding: 28px; border-radius: 20px; background: #f4f8fb; }
.control-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 13px; }
.control-grid article { display: flex; align-items: flex-start; min-height: 124px; padding: 18px; border: 1px solid #e5edf3; border-radius: 14px; background: #fff; }
.control-grid article > span { width: 38px; height: 38px; flex: 0 0 38px; display: grid; place-items: center; margin-right: 13px; border-radius: 11px; background: #e7f7f5; color: var(--trust-teal); }
.control-grid h3 { margin: 2px 0 6px; color: var(--trust-navy); font-size: 14px; font-weight: 800; }
.control-grid p { margin: 0; color: #64748b; font-size: 11px; line-height: 1.6; }

.standard-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
.standard-grid article { padding: 22px; border: 1px solid var(--trust-border); border-radius: 17px; background: #fff; }
.standard-card__top > span { color: var(--trust-teal); font-size: 13px; font-weight: 900; letter-spacing: .08em; }
.standard-card__top em { padding: 5px 8px; border-radius: 99px; background: #eef2f7; color: #64748b; font-size: 10px; font-style: normal; font-weight: 800; }
.standard-grid h3 { margin: 17px 0 8px; color: var(--trust-navy); font-size: 17px; }
.standard-grid p { min-height: 76px; margin: 0 0 17px; color: #64748b; font-size: 12px; line-height: 1.65; }
.standard-grid a i { margin-left: 5px; font-size: 9px; }

.responsibility-section { display: grid; grid-template-columns: .8fr 1.4fr; gap: 34px; margin-top: 36px; padding: 30px; border-radius: 20px; background: var(--trust-navy); color: #fff; }
.responsibility-intro h2 { color: #fff !important; }
.responsibility-intro p { color: #d3dfeb !important; }
.responsibility-columns { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.responsibility-columns article { padding: 20px; border: 1px solid rgba(255,255,255,.12); border-radius: 15px; background: rgba(255,255,255,.07); }
.responsibility-columns header { display: flex; align-items: center; margin-bottom: 14px; }
.responsibility-columns header span { width: 35px; height: 35px; display: grid; place-items: center; margin-right: 10px; border-radius: 10px; background: rgba(94,234,212,.12); color: #5eead4; }
.responsibility-columns h3 { margin: 0; color: #fff !important; font-size: 14px; }
.responsibility-columns ul { margin: 0; padding: 0; list-style: none; }
.responsibility-columns li { display: flex; margin-top: 10px; color: #e3ebf3 !important; font-size: 11px; line-height: 1.55; }
.responsibility-columns li i { margin: 3px 8px 0 0; color: #5eead4; font-size: 9px; }

.disclosure-note { display: flex; margin-top: 18px; padding: 20px 22px; border: 1px solid #dbe7ee; border-radius: 15px; background: #f8fbfd; }
.disclosure-note > i { margin: 2px 13px 0 0; color: #3b82f6; font-size: 17px; }
.disclosure-note h3 { margin: 0 0 5px; color: var(--trust-navy); font-size: 13px; }
.disclosure-note p { margin: 0; color: #64748b; font-size: 11px; line-height: 1.65; }

.trust-cta { display: flex; align-items: center; justify-content: space-between; gap: 24px; margin-top: 18px; padding: 25px 28px; border: 1px solid #cde9e6; border-radius: 17px; background: linear-gradient(100deg, #ecf9f7, #f8fbfd); }
.trust-cta span { color: #168f88; font-size: 11px; font-weight: 800; }
.trust-cta h2 { margin: 4px 0 0; color: var(--trust-navy); font-size: 18px; }
.trust-cta .btn { flex: 0 0 auto; border-radius: 10px; background: var(--trust-teal); border-color: var(--trust-teal); font-weight: 700; }

@media (max-width: 1100px) {
  .trust-hero { grid-template-columns: 1fr; }
  .pse-card { min-height: auto; }
  .trust-summary, .regulation-grid, .control-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .responsibility-section { grid-template-columns: 1fr; }
}

@media (max-width: 680px) {
  .trust-hero { padding: 25px 21px; }
  .trust-summary, .regulation-grid, .control-grid, .standard-grid, .responsibility-columns { grid-template-columns: 1fr; }
  .controls-section, .responsibility-section { padding: 22px 18px; }
  .regulation-card { min-height: 235px; }
  .trust-cta { align-items: flex-start; flex-direction: column; }
  .trust-cta .btn { width: 100%; }
}
</style>
